import React, { useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, LineChart, Line,
} from 'recharts';
import { useHistory } from '../hooks/useData';
import { Spinner, curSym } from './UI';

const PERIODS = [
  { label: '5G', value: '5d' }, { label: '1M', value: '1mo' },
  { label: '3M', value: '3mo' }, { label: '6M', value: '6mo' },
  { label: '1A', value: '1y' }, { label: '2A', value: '2y' },
];

const CustomTooltip = ({ active, payload, label, curSymbol = '€' }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{ background: '#0d1117', border: '1px solid #1e2736', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#e2e8f0', fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{curSymbol} {d?.close?.toFixed(3)}</div>
      {d?.sma20 && <div style={{ color: '#f59e0b' }}>SMA 20: {curSymbol}{d.sma20.toFixed(3)}</div>}
      {d?.sma50 && <div style={{ color: '#8b5cf6' }}>SMA 50: {curSymbol}{d.sma50.toFixed(3)}</div>}
      <div style={{ color: '#475569', marginTop: 4 }}>Vol: {(d?.volume / 1e6).toFixed(1)}M</div>
    </div>
  );
};

function calcSMA(data, n) {
  return data.map((_, i) => {
    if (i < n - 1) return null;
    const slice = data.slice(i - n + 1, i + 1);
    return parseFloat((slice.reduce((s, d) => s + d.close, 0) / n).toFixed(4));
  });
}

export default function PriceChart({ ticker, market, technicalData, currency = 'EUR' }) {
  const [period, setPeriod] = useState('3mo');
  const [showSMA20, setShowSMA20] = useState(true);
  const [showSMA50, setShowSMA50] = useState(true);

  const { data: raw, loading, error } = useHistory(ticker, period, market);

  const data = React.useMemo(() => {
    if (!raw?.length) return [];
    const sma20 = calcSMA(raw, 20);
    const sma50 = calcSMA(raw, 50);
    return raw.map((d, i) => ({
      ...d,
      label: d.date?.slice(5),
      sma20: sma20[i],
      sma50: sma50[i],
    }));
  }, [raw]);

  const minPrice = data.length ? Math.min(...data.map(d => d.low)) * 0.995 : 0;
  const maxPrice = data.length ? Math.max(...data.map(d => d.high)) * 1.005 : 100;
  const isUp = data.length > 1 && data[data.length - 1].close >= data[0].close;

  const gradientId = `grad_${ticker}`;
  const lineColor = isUp ? '#10b981' : '#f87171';
  const gradFrom  = isUp ? 'rgba(16,185,129,0.18)' : 'rgba(248,113,113,0.18)';

  const support = technicalData?.levels?.support_1;
  const resist  = technicalData?.levels?.resistance_1;
  const cs = curSym(currency);

  const tickCount = data.length > 60 ? 6 : data.length > 20 ? 5 : 4;
  const step = Math.floor(data.length / tickCount);
  const ticks = data.filter((_, i) => i % step === 0).map(d => d.date);

  if (loading) return <Spinner />;
  if (error) {
    const m = error.match(/\{"detail":"(.+?)"\}/);
    const msg = m ? m[1] : error;
    return (
      <div style={{ padding: '16px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, color: '#fbbf24', fontSize: 13, lineHeight: 1.6 }}>
        ⚠ {msg}
      </div>
    );
  }
  if (!data.length) return <div style={{ color: '#475569', fontSize: 13, padding: 20 }}>Nessun dato disponibile</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)} style={{
              fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
              background: period === p.value ? '#1e3a5f' : 'transparent',
              color: period === p.value ? '#60a5fa' : '#475569',
              border: period === p.value ? '1px solid #2563eb' : '1px solid transparent',
              fontWeight: period === p.value ? 600 : 400,
            }}>{p.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {[{ label: 'SMA 20', color: '#f59e0b', active: showSMA20, toggle: () => setShowSMA20(v => !v) },
            { label: 'SMA 50', color: '#8b5cf6', active: showSMA50, toggle: () => setShowSMA50(v => !v) }]
            .map(ind => (
              <button key={ind.label} onClick={ind.toggle} style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                background: ind.active ? `${ind.color}18` : 'transparent',
                color: ind.active ? ind.color : '#475569',
                border: `1px solid ${ind.active ? ind.color + '60' : '#1e2736'}`,
              }}>{ind.label}</button>
            ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={lineColor} stopOpacity={0.18} />
              <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" vertical={false} />
          <XAxis dataKey="date" ticks={ticks} tick={{ fill: '#475569', fontSize: 10 }}
            tickLine={false} axisLine={false} tickFormatter={v => v?.slice(5)} />
          <YAxis domain={[minPrice, maxPrice]} tick={{ fill: '#475569', fontSize: 10 }}
            tickLine={false} axisLine={false} tickFormatter={v => `${cs}${v.toFixed(2)}`}
            width={60} orientation="right" />
          <Tooltip content={<CustomTooltip curSymbol={cs} />} />
          {support && <ReferenceLine y={support} stroke="#10b981" strokeDasharray="4 4" strokeOpacity={0.5} />}
          {resist  && <ReferenceLine y={resist}  stroke="#f87171" strokeDasharray="4 4" strokeOpacity={0.5} />}
          <Area dataKey="close" stroke={lineColor} strokeWidth={1.5} fill={`url(#${gradientId})`} dot={false} name="Prezzo" />
          {showSMA20 && <Line dataKey="sma20" stroke="#f59e0b" strokeWidth={1} dot={false} name="SMA 20" />}
          {showSMA50 && <Line dataKey="sma50" stroke="#8b5cf6" strokeWidth={1} dot={false} name="SMA 50" />}
        </AreaChart>
      </ResponsiveContainer>

      <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11, color: '#475569' }}>
        <span style={{ color: '#10b981' }}>— Supporto: {cs}{support?.toFixed(2) ?? '—'}</span>
        <span style={{ color: '#f87171' }}>— Resistenza: {cs}{resist?.toFixed(2) ?? '—'}</span>
      </div>
    </div>
  );
}
